import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { UpdateRoomMaintenanceForm, UpdateRoomMaintenanceSchema } from '../../../services/room-maintenance/schema/UpdateRoomMaintenanceSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import useCheckAvi from '../../../services/booking/hooks/useCheckAvi';
import useShowRoomMaintenance from '../../../services/room-maintenance/hooks/useShowRoomMaintenance';
import { useUpdateRoomMaintenance } from '../../../services/room-maintenance/hooks/useUpdateRoomMaintenance';
import { useDeleteRoomMaintenance } from '../../../services/room-maintenance/hooks/useDeleteRoomMaintenancen';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import clsx from 'clsx';
import { Control, DeepMap, FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import Select from 'react-select';
import { CheckAviResponseType, RoomTypeData } from '../../../services/booking/response/CheckAviResponse';
import FormError from '../../../shared/components/errors/FormError';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import ConfirmationDialog from '../../../shared/components/dialog/ConfirmationDialog';
import { extractErrorMessage } from '../../../shared/utils/serverValidation';
import DateInput from '../../../shared/components/form/DateInput';

interface UpdateRoomMaintenanceDialogProps {
    onClose: () => void,
    id: string
}

const UpdateRoomMaintenanceComponent: React.FC<UpdateRoomMaintenanceDialogProps> = ({ onClose, id }) => {
    const { data: roomMaintenance, isFetching: isRoomMaintenanceFetching, isError: isRoomMaintenanceError, isSuccess: isRoomMaintenanceSuccess } = useShowRoomMaintenance({
        id: id,
        include: 'property,roomType,room'
    });

    const { register, handleSubmit, control, setValue, reset, formState: { errors }, watch, setError } = useForm<UpdateRoomMaintenanceForm>({
        resolver: zodResolver(UpdateRoomMaintenanceSchema),
    })

    useEffect(() => {
        if (roomMaintenance) {
            reset({
                checkin_date: roomMaintenance.data.checkin_date,
                checkout_date: roomMaintenance.data.checkout_date,
                room_type_id: roomMaintenance.data.room_type_id,
                room_id: roomMaintenance.data.room_id,
                note: roomMaintenance.data.note ?? '',
            });
        }
    }, [isRoomMaintenanceSuccess, roomMaintenance]);

    // room_maintenance_id: exclude blok maintenance yang sedang diedit dari
    // pengecekan ketersediaan-nya sendiri (lihat RoomRateController::checkRoomRate
    // di backend) — tanpa ini kamar yang sedang di-maintenance akan selalu
    // dianggap tidak tersedia oleh dirinya sendiri, walau tanggal tidak diubah.
    const { data: roomAvi, isFetching, isError, error, isSuccess } = useCheckAvi({
        checkin_date: watch('checkin_date'),
        checkout_date: watch('checkout_date'),
        room_maintenance_id: id,
    });

    // Di-memoize supaya referensinya stabil — dipakai sebagai dependency oleh
    // useCallback handleDelete (dan alur submit), yang tanpa ini akan berubah
    // identitasnya di setiap render.
    const handleClose = useCallback(() => {
        reset()
        onClose()
    }, [reset, onClose])

    useEffect(() => {
        return () => {
            reset();
        }
    }, [])

    const { mutate, isPending } = useUpdateRoomMaintenance();
    const { mutate: deleteMutate, isPending: isDeleting } = useDeleteRoomMaintenance();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const queryClient = useQueryClient();

    /**
     * Menghapus blok maintenance. Ketersediaan kamar TIDAK perlu di-update manual:
     * backend (`RoomMaintenanceController::destroy`) sudah men-dispatch
     * SyncChannexAvailabilityJob + SyncChannexRestrictionsJob untuk mengembalikan
     * ketersediaan ke OTA, sedangkan ketersediaan lokal dihitung on-the-fly oleh
     * `AvailabilityHelper` yang nge-join tabel room_maintenances secara live —
     * begitu barisnya hilang, angkanya otomatis ikut berubah.
     *
     * Yang jadi tanggung jawab FE cuma refetch. Predikatnya sengaja disamakan
     * dengan alur update ('bookings' + 'room-maintenance') supaya kalender
     * reservasi maupun halaman room maintenance sama-sama ikut ter-refresh.
     */
    const handleDelete = useCallback(() => {
        toast.loading('Deleting room maintenance...', { id: 'delete-room-maintenance' });

        deleteMutate({ id }, {
            onSuccess: async () => {
                setShowDeleteConfirm(false);
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['bookings', 'room-maintenance'].includes(key as string))
                });

                toast.success('Room maintenance deleted successfully', { id: 'delete-room-maintenance' });
            },
            onError: (mutationError) => {
                setShowDeleteConfirm(false);
                toast.error(
                    extractErrorMessage(mutationError, 'Failed to delete room maintenance'),
                    { id: 'delete-room-maintenance' }
                );
            }
        });
    }, [deleteMutate, id, handleClose, queryClient]);

    const onSubmit = useCallback((values: UpdateRoomMaintenanceForm) => {
        toast.loading('Updating room maintenance...', { id: 'update-room-maintenance' });

        mutate({
            id: id,
            formData: values
        }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['bookings', 'room-maintenance'].includes(key as string))
                });

                toast.success('Room maintenance updated successfully', { id: 'update-room-maintenance' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdateRoomMaintenanceForm, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update room maintenance', { id: 'update-room-maintenance' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to update room maintenance', { id: 'update-room-maintenance' });
                }
            }
        });
    }, [mutate]);

    return (
        <>
            {isRoomMaintenanceError && <FormError />}
            {isRoomMaintenanceFetching && (<FormSpinner />)}
            {isRoomMaintenanceSuccess && !isRoomMaintenanceFetching && (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row mb-3 align-items-start">
                        <div className="col-sm-3">
                            <label htmlFor="checkin_date" className="col-sm-4 col-form-label d-flex" style={{ fontSize: '14px' }}>Checkin Date:</label>
                            <DateInput
                                style={{ fontSize: '14px', height: '35px' }}
                                {...register('checkin_date')}
                            />
                            {errors.checkin_date && <span className="text-danger">{errors.checkin_date.message}</span>}
                        </div>
                        <div className="col-sm-3">
                            <label htmlFor="checkout_date" className="col-sm-4 col-form-label d-flex" style={{ fontSize: '14px' }}>Checkout Date:</label>
                            <DateInput
                                style={{ fontSize: '14px', height: '35px' }}
                                {...register('checkout_date')}
                            />
                            {errors.checkout_date && <span className="text-danger">{errors.checkout_date.message}</span>}
                        </div>
                        {/* room type id */}
                        <div className="col-sm-3">
                            {!isSuccess && !isFetching && !isError && (
                                <div className="col-12">
                                    <label className="col-form-label" htmlFor="room">Room:</label>
                                    <Select
                                        options={[]}
                                        isDisabled={true}
                                        placeholder="Select checkin and checkout date"
                                    />
                                </div>
                            )}
                            {isError && <span className="text-danger">{error?.message}</span>}
                            {isFetching && (
                                <div className="col-12">
                                    <label className="col-form-label" htmlFor="room">Room:</label>
                                    <Select
                                        options={[]}
                                        isLoading={true}
                                        isDisabled={true}
                                        placeholder="Loading..."
                                    />
                                </div>
                            )}
                            {isSuccess && !isFetching && (
                                <SelectRoomType control={control} roomType={roomAvi} setValue={setValue} errors={errors} />
                            )}
                        </div>
                        {/* room id */}
                        <div className="col-sm-3">
                            {watch('room_type_id') === undefined && (
                                <div className="col-12">
                                    <label className="col-form-label" htmlFor="subroom">Subroom:</label>
                                    <Select
                                        options={[]}
                                        isDisabled={true}
                                        placeholder="Select room first"
                                    />
                                </div>
                            )}
                            {isSuccess && !isFetching && watch('room_type_id') && (
                                <SubroomComponent watch={watch} control={control} index={0} roomType={roomAvi} errors={errors} />
                            )}
                        </div>
                    </div>
                    <div className="row mb-3 align-items-start">
                        <div className="col-sm-12">
                            <label htmlFor="note" className="col-sm-2 col-form-label d-flex" style={{ fontSize: '14px' }}>Note:</label>
                            <textarea
                                className="form-control app-form-control rounded-1"
                                style={{ fontSize: '14px' }}
                                placeholder="Note"
                                {...register('note')}
                            />
                            {errors.note && <span className="text-danger">{errors.note.message}</span>}
                        </div>
                    </div>
                    <div className="my-4 border-bottom"></div>
                    <div className="d-flex flex-column justify-content-end gap-2">
                        <div className="d-flex justify-content-between gap-2">
                            <button type="button" className="btn btn-secondary" onClick={() => handleClose()}>
                                Cancel
                            </button>
                            <div className="d-flex gap-2">
                                {/* type="button" wajib — tanpa itu tombol ini ikut men-submit
                                    form update (default tombol di dalam <form>). */}
                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_maintenance_delete']} asComponent>
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={isPending || isDeleting}
                                    >
                                        Delete
                                    </button>
                                </RequireAuth>
                                <button type="submit" className="btn btn-primary" disabled={isPending || isDeleting}>
                                    Update Room Maintenance
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            )}

            {/* Aksi destruktif & tidak bisa di-undo, jadi selalu lewat konfirmasi.
                Modal react-bootstrap di-portal ke body, sehingga aman walau
                pemicunya berada di dalam <form> dan di dalam Offcanvas. */}
            <ConfirmationDialog
                show={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="Delete Room Maintenance"
                message="This maintenance block will be deleted and the room will become available for sale again. This action cannot be undone. Continue?"
                confirmButtonText="Delete"
                confirmButtonVariant="danger"
                isConfirming={isDeleting}
            />
        </>
    )
}

export default UpdateRoomMaintenanceComponent

interface SelectRoomTypeProps {
    control: Control<any>
    roomType: CheckAviResponseType
    setValue: UseFormSetValue<any>
    errors: DeepMap<any, FieldError>
}

interface RoomType {
    value: string,
    label: string
}

const SelectRoomType: React.FC<SelectRoomTypeProps> = ({ control, roomType, setValue, errors }) => {
    const [roomTypeOptions, setRoomTypeOptions] = useState<RoomType[]>([]);

    useEffect(() => {
        if (roomType.data.length > 0) {
            setRoomTypeOptions(roomType.data.map((room: { id: string, name: string }) => ({
                value: room.id,
                label: room.name
            })))
        }
    }, [roomType])

    return (
        <div className="col-12">
            <label className="col-form-label" htmlFor="room">Room:</label>
            <Controller
                control={control}
                name={'room_type_id'}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.room_type_id })}
                        options={roomTypeOptions}
                        value={roomTypeOptions.find((room) => room.value === field.value)}
                        onChange={(selectedOption) => {
                            if (selectedOption) {
                                field.onChange(selectedOption.value);
                                setValue(`room_id`, '');
                            }
                        }}
                        noOptionsMessage={() => 'No rooms available'}
                    />
                )}
            />
            {errors.room_type_id && (
                <div className="invalid-feedback">
                    {errors.room_type_id.message}
                </div>
            )}
        </div>
    )
}

interface SubroomComponentProps {
    watch: UseFormWatch<any>,
    control: Control<any>,
    index: number,
    roomType: CheckAviResponseType,
    errors: DeepMap<any, FieldError>
}

interface Subroom {
    value: string,
    label: string
}

const SubroomComponent: React.FC<SubroomComponentProps> = ({ watch, control, roomType, errors }) => {
    const [subroomOptions, setSubroomOptions] = useState<Subroom[]>([]);

    useEffect(() => {
        const roomTypeId = watch(`room_type_id`);
        if (roomTypeId) {
            const roomData = roomType.data.find((room: RoomTypeData) => room.id === roomTypeId);
            if (roomData) {
                let rooms = roomData.rooms?.map((room: { id: string, room_number: string }) => ({
                    value: room.id,
                    label: room.room_number
                })) || [];

                // Jika tidak ada room, tambahkan opsi "Unassigned Room"
                if (rooms.length === 0) {
                    rooms = [{ value: '', label: "Unassigned Room" }];
                }

                setSubroomOptions(rooms);
            }
        }
    }, [watch(`room_type_id`)]);

    return (
        <div className="col-12">
            <label className="col-form-label" htmlFor="subroom">Subroom:</label>
            <Controller
                control={control}
                name={`room_id`}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.room_id })}
                        options={subroomOptions}
                        value={subroomOptions.find((room) => room.value === field.value) || null}
                        onChange={(selectedOption) => {
                            field.onChange(selectedOption?.value ?? null);
                        }}
                        noOptionsMessage={() => 'No rooms available'}
                    />
                )}
            />
            {errors.room_id && (
                <div className="invalid-feedback">
                    {errors.room_id.message}
                </div>
            )}
        </div>
    );
};